import * as _4 from 'lodash4';
import { ConditionalStrategySafe, StrategySafe } from 'utility-ts-strategy';
import { IAPSuggestedPlanItem, proposer } from '../builders/ap_changelog_builder';
import { APChangelogServant } from '../servants/ap_changelog_servant';
export interface IAPChangelogStrategy {
  css: string;
  title: string;
  type: string;
  items: Array<{
    displaySentence: string;
    commentDetails: ICommentDetails;
  }>;
}

type ResolutionTypes = 'ADVISOR' | 'STUDENT' | 'UNDECIDED' | 'CONFLICTING';
type SuggestionStatusTypes = 'accepted' | 'declined' | 'pending';

export const SuggestionTypeStrategy = new StrategySafe<IAPChangelogStrategy, ResolutionTypes>({
  ADVISOR: {
    css: 'clr-green_50-txt',
    title: 'Accepted',
    type: 'accept',
    items: []
  },
  STUDENT: {
    css: 'clr-red_30-txt',
    title: 'Declined',
    type: 'decline',
    items: []
  },
  UNDECIDED: {
    title: 'Pending',
    css: '',
    type: 'pending',
    items: []
  },
  CONFLICTING: {
    title: 'Conflicting',
    css: '',
    type: 'conflicting',
    items: []
  }
});

export const PROPOSER: Record<string, proposer> = {
  advisor: `ADVISOR`,
  student: `STUDENT`
};
export type TCommentRelatedChangeType = 'MET' | 'UNMET' | 'COMMENT_ONLY';
export type ChangeType = 'MOVE' | 'ADD' | 'REMOVE' | 'DEFAULT' | TCommentRelatedChangeType;

const isMoveSuggestedPlanItem = (changes: IAPSuggestedPlanItem[]) => {
  if (changes.length !== 2) {
    return false;
  }
  if (_4.isEmpty(changes[0].term)) {
    return false;
  }
  const hasTermChanged = !_4.isEqual(_4.first(changes).term.id, _4.last(changes).term.id);
  // it is weird that api is sending the comment only update for flagged transaction like this
  const onlyCommentChanged = !hasTermChanged && !_4.isEqual(_4.first(changes).comment, _4.last(changes).comment);
  return hasTermChanged || onlyCommentChanged;
};

const isTemplateTransaction = (changes: IAPSuggestedPlanItem[], _proposer: proposer) => {
  if (changes.length !== 1) {
    return false;
  }
  const transaction = _4.first(changes);
  if (transaction.slot_status !== 'MET') {
    return false;
  }

  return transaction.proposer === _proposer;
};

export const ChangeTypeStrategy = new ConditionalStrategySafe<IAPSuggestedPlanItem[], ChangeType>({
  MOVE: (changes: IAPSuggestedPlanItem[]) => isMoveSuggestedPlanItem(changes),
  MET: (changes: IAPSuggestedPlanItem[]) => isTemplateTransaction(changes, 'ADVISOR'),
  UNMET: (changes: IAPSuggestedPlanItem[]) => isTemplateTransaction(changes, 'STUDENT'),
  COMMENT_ONLY: (changes: IAPSuggestedPlanItem[]) =>
    changes.length === 2 && _4.first(changes).slot_status === 'COMMENT_ONLY',
  ADD: (changes: IAPSuggestedPlanItem[]) => !APChangelogServant.getProposerChange(changes, PROPOSER.student),
  REMOVE: (changes: IAPSuggestedPlanItem[]) => !APChangelogServant.getProposerChange(changes, PROPOSER.advisor),
  DEFAULT: () => true
});

export interface IChangeTypeTemplate {
  commentTemplate: (changes: IAPSuggestedPlanItem[]) => ICommentDetails;
  courseTemplate: (changes: IAPSuggestedPlanItem[]) => string[];
  lockTemplate: (changes: IAPSuggestedPlanItem[]) => string;
}
export interface ICommentDetails {
  comment?: string;
  prefix: string;
  hasFlag: boolean;
}

const lockedText = (isLocked: boolean) => (isLocked ? 'locked' : 'unlocked');

const getLockedTemplate = (suggestedChanges: IAPSuggestedPlanItem[]) => {
  const lockChanged = APChangelogServant.hasPropertyChanged(`is_locked`, suggestedChanges);
  const hasAdvisorLock = APChangelogServant.getProposerChangeProperty(suggestedChanges, PROPOSER.advisor, `is_locked`);
  if (!lockChanged) {
    return ``;
  }
  return `was ${lockedText(!!hasAdvisorLock)}`;
};

const getSlotTitle = (courseForTemplate: IAPSuggestedPlanItem) => {
  const courseTemplateForDataMissing = `<span class='text-italic'>Missing course data</span>`;
  if (!courseForTemplate) {
    return ``;
  }

  if (APChangelogServant.isCourse(courseForTemplate)) {
    const cd = _4.get(courseForTemplate, 'course.cd', courseTemplateForDataMissing);
    const title = _4.get(courseForTemplate, 'course.title', '');
    return `${cd} ${title}`;
  }
  const _slotTile = APChangelogServant.getSlotTitle(courseForTemplate);
  return _4.isEmpty(_slotTile) ? `${courseTemplateForDataMissing}` : _slotTile;
};

export const ChangeTypeTemplateStrategy = new StrategySafe<
  IChangeTypeTemplate,
  Exclude<ChangeType, TCommentRelatedChangeType>
>({
  MOVE: {
    commentTemplate: (suggestedChanges) => {
      const advisorChange = APChangelogServant.getProposerChange(suggestedChanges, PROPOSER.advisor);
      const studentChange = APChangelogServant.getProposerChange(suggestedChanges, PROPOSER.student);
      const hasAdvisorComment = APChangelogServant.hasComment(advisorChange);
      if (!APChangelogServant.hasTermChanged(suggestedChanges)) {
        return {
          comment: hasAdvisorComment ? advisorChange.comment : '',
          prefix: `edited`,
          hasFlag: true
        };
      } else {
        return {
          comment: hasAdvisorComment ? advisorChange.comment : studentChange.comment,
          prefix: hasAdvisorComment ? `new` : `removed`,
          hasFlag: hasAdvisorComment
        };
      }
    },
    courseTemplate: (suggestedChanges: IAPSuggestedPlanItem[]) => {
      const courseForTemplate = APChangelogServant.getProposerChange(suggestedChanges, PROPOSER.advisor);
      const courseTitle = getSlotTitle(courseForTemplate);

      let suffixText: string = '';
      const fromTerm = _4.get(
        APChangelogServant.getProposerChangeProperty(suggestedChanges, PROPOSER.student, `term`),
        'name'
      );
      const toTerm = _4.get(
        APChangelogServant.getProposerChangeProperty(suggestedChanges, PROPOSER.advisor, `term`),
        'name'
      );
      suffixText = !APChangelogServant.hasTermChanged(suggestedChanges) ? '' : `moved from ${fromTerm} to ${toTerm}`;
      return [courseTitle, `${suffixText}`];
    },
    lockTemplate: (suggestedChanges) => getLockedTemplate(suggestedChanges)
  },
  ADD: {
    commentTemplate: (suggestedChanges): ICommentDetails => {
      const advisorChange = APChangelogServant.getProposerChange(suggestedChanges, PROPOSER.advisor);
      const hasAdvisorComment = APChangelogServant.hasComment(advisorChange);
      return {
        comment: hasAdvisorComment ? advisorChange.comment : '',
        prefix: `new`,
        hasFlag: true
      };
    },
    courseTemplate: (suggestedChanges) => {
      const courseForTemplate = APChangelogServant.getProposerChange(suggestedChanges, PROPOSER.advisor);
      const courseTitle = getSlotTitle(courseForTemplate);
      const suffixText: string = `added to ${courseForTemplate.term.name}`; // toTerm?;
      return [`${courseTitle}`, `${suffixText}`];
    },
    lockTemplate: (suggestedChanges) => {
      const hasAdvisorLock = APChangelogServant.getProposerChangeProperty(
        suggestedChanges,
        PROPOSER.advisor,
        `is_locked`
      );
      return hasAdvisorLock ? `was locked` : ``;
    }
  },
  REMOVE: {
    commentTemplate: (suggestedChanges) => {
      const studentChange = APChangelogServant.getProposerChange(suggestedChanges, PROPOSER.student);
      return {
        comment: APChangelogServant.hasComment(studentChange) ? studentChange.comment : '',
        prefix: `removed`,
        hasFlag: false
      };
    },
    courseTemplate: (suggestedChanges) => {
      const courseForTemplate = APChangelogServant.getProposerChange(suggestedChanges, PROPOSER.student);
      const courseTitle = getSlotTitle(courseForTemplate);
      const suffixText: string = `removed from ${courseForTemplate.term.name}`; // fromTerm?
      return [`${courseTitle}`, `${suffixText}`];
    },
    lockTemplate: (suggestedChanges) => {
      const hasAdvisorLock = APChangelogServant.getProposerChangeProperty(
        suggestedChanges,
        PROPOSER.advisor,
        `is_locked`
      );
      return hasAdvisorLock ? `was unlocked` : ``;
    }
  },
  DEFAULT: {
    commentTemplate: (suggestedChanges) => {
      return {
        comment: '',
        prefix: `new`,
        hasFlag: false
      };
    },
    courseTemplate: (suggestedChanges) => {
      const courseForTemplate = APChangelogServant.getProposerChange(suggestedChanges, PROPOSER.advisor);
      return [`${getSlotTitle(courseForTemplate)}`];
    },
    lockTemplate: (suggestedChanges) => getLockedTemplate(suggestedChanges)
  }
});



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/changelog/strategies/ap_changelog_strategy.ts